import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Package
} from 'lucide-react';
import adminService from '../../api/services/adminService';
import { toPriceObject } from '../../api/utils/price';

const BUNDLE_TYPES = ['phase_bundle', 'full_collection', 'teacher_pack', 'classroom_pack', 'starter_kit', 'training_bundle', 'school_adoption'];
const BUNDLE_STATUSES = ['active', 'inactive', 'coming_soon'];
const TARGET_BUYERS = ['all', 'parents', 'educators', 'schools'];
const CURRENCIES = ['USD'];
const normalizeAdminCurrency = (currency) => (currency === 'AED' || currency === 'CAD' ? 'USD' : (currency || 'USD'));

const EMPTY_PRICE = { currency: 'USD', bundlePrice: '', individualTotal: '' };
const EMPTY_ITEM = { product: '', quantity: 1, isRequired: true };

const EMPTY_FORM = {
  name: '',
  slug: '',
  description: '',
  type: 'phase_bundle',
  targetBuyer: 'all',
  status: 'active',
  phaseNumber: '',
  ageRange: '',
  containsDigital: false,
  savingsPercentage: '',
  savingsMessage: '',
  isCustomQuote: false,
  image: '',
  featured: false,
  items: [{ ...EMPTY_ITEM }],
  prices: [{ ...EMPTY_PRICE }],
};

const BundlesPage = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [booksCatalog, setBooksCatalog] = useState([]);
  const [booksLoading, setBooksLoading] = useState(true);
  const [bundleImageFile, setBundleImageFile] = useState(null);
  const [bundleImagePreview, setBundleImagePreview] = useState('');

  useEffect(() => {
    fetchBundles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  useEffect(() => {
    fetchBooksCatalog();
  }, []);

  const fetchBundles = async () => {
    try {
      setLoading(true);
      const response = await adminService.getBundles({ page: pagination.page, limit: 10 });
      setBundles(response.bundles || []);
      if (response.pagination) setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  const fetchBooksCatalog = async () => {
    try {
      setBooksLoading(true);
      const response = await adminService.getBooks({ page: 1, limit: 250 });
      const books = Array.isArray(response.data) ? response.data : [];
      const sortedBooks = [...books].sort((left, right) => {
        const phaseDiff = (left.phase || 0) - (right.phase || 0);
        if (phaseDiff !== 0) return phaseDiff;
        const courseDiff = (left.course || 0) - (right.course || 0);
        if (courseDiff !== 0) return courseDiff;
        return (left.title || '').localeCompare(right.title || '');
      });
      setBooksCatalog(sortedBooks);
    } catch (_) {
      setBooksCatalog([]);
    } finally {
      setBooksLoading(false);
    }
  };

  const slugify = (str) =>
    str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const openCreate = () => {
    setEditingBundle(null);
    setFormData({ ...EMPTY_FORM, items: [{ ...EMPTY_ITEM }], prices: [{ ...EMPTY_PRICE }] });
    setFormError('');
    setBundleImageFile(null);
    setBundleImagePreview('');
    setIsModalOpen(true);
  };

  const openEdit = (bundle) => {
    setEditingBundle(bundle);
    setFormData({
      name: bundle.name || '',
      slug: bundle.slug || '',
      description: bundle.description || '',
      type: bundle.type || 'phase_bundle',
      targetBuyer: bundle.targetBuyer || 'all',
      status: bundle.status || 'active',
      phaseNumber: bundle.phaseNumber ?? '',
      ageRange: bundle.ageRange || '',
      containsDigital: !!bundle.containsDigital,
      savingsPercentage: bundle.savingsPercentage ?? '',
      savingsMessage: bundle.savingsMessage || '',
      isCustomQuote: bundle.isCustomQuote || false,
      image: bundle.image || '',
      featured: !!bundle.featured,
      items: bundle.items?.length
        ? bundle.items.map(item => ({
            product: item.product?._id || item.product || '',
            quantity: item.quantity || 1,
            isRequired: item.isRequired !== false,
          }))
        : [{ ...EMPTY_ITEM }],
      prices: bundle.prices?.length
        ? bundle.prices.map(p => ({ currency: normalizeAdminCurrency(p.currency), bundlePrice: p.bundlePrice ?? '', individualTotal: p.individualTotal ?? '' }))
        : [{ ...EMPTY_PRICE }],
    });
    setFormError('');
    setBundleImageFile(null);
    setBundleImagePreview(bundle.image || '');
    setIsModalOpen(true);
  };

  const addPrice = () => setFormData(d => ({ ...d, prices: [...d.prices, { ...EMPTY_PRICE }] }));
  const removePrice = (i) => setFormData(d => ({ ...d, prices: d.prices.filter((_, idx) => idx !== i) }));
  const updatePrice = (i, field, value) => setFormData(d => ({
    ...d,
    prices: d.prices.map((p, idx) => idx === i ? { ...p, [field]: value } : p)
  }));
  const addItem = () => setFormData(d => ({ ...d, items: [...d.items, { ...EMPTY_ITEM }] }));
  const removeItem = (i) => setFormData(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, field, value) => setFormData(d => ({
    ...d,
    items: d.items.map((item, idx) => idx === i ? { ...item, [field]: value } : item)
  }));

  const getBookById = (id) => booksCatalog.find((book) => book._id === id);
  const selectedItems = formData.items.filter((item) => item.product);
  const selectedBookCount = selectedItems.reduce((total, item) => total + (Number(item.quantity) || 1), 0);
  const selectedRetailTotal = selectedItems.reduce((total, item) => {
    const book = getBookById(item.product);
    if (!book) return total;
    const priceSource = book.prices?.[0] || book.price;
    const priceObj = toPriceObject(priceSource, book.currency || 'USD');
    return total + ((priceObj?.retail || 0) * (Number(item.quantity) || 1));
  }, 0);
  const hasDuplicateBooks = new Set(selectedItems.map((item) => item.product)).size !== selectedItems.length;

  const useSelectedBooksTotal = () => {
    setFormData((current) => ({
      ...current,
      prices: current.prices.map((price, index) => index === 0 ? { ...price, individualTotal: selectedRetailTotal.toFixed(2) } : price),
    }));
  };

  const formatBookOption = (book) => {
    const meta = [`P${book.phase || '—'}`, `C${book.course || '—'}`, book.status?.replace(/_/g, ' ') || 'unknown'];
    return `${book.title} (${meta.join(' • ')})`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    const items = formData.items.filter((item) => item.product);
    if (items.length === 0) {
      setFormError('Select at least one book for this bundle');
      return;
    }
    if (new Set(items.map((item) => item.product)).size !== items.length) {
      setFormError('Each book can only appear once. Increase quantity instead of adding duplicates.');
      return;
    }

    setFormLoading(true);
    try {
      let imageUrl = formData.image;
      if (bundleImageFile) {
        const uploadRes = await adminService.uploadBundleImage(bundleImageFile);
        imageUrl = uploadRes.url;
      }

      const payload = {
        ...formData,
        slug: formData.slug || slugify(formData.name),
        savingsPercentage: formData.savingsPercentage !== '' ? parseFloat(formData.savingsPercentage) : undefined,
        phaseNumber: formData.phaseNumber !== '' ? parseInt(formData.phaseNumber, 10) : undefined,
        image: imageUrl,
        items: items.map(item => ({
          product: item.product,
          quantity: item.quantity !== '' ? parseInt(item.quantity, 10) || 1 : 1,
          isRequired: item.isRequired !== false,
        })),
        prices: formData.prices.map(p => ({
          currency: p.currency,
          bundlePrice: p.bundlePrice !== '' ? parseFloat(p.bundlePrice) : 0,
          individualTotal: p.individualTotal !== '' ? parseFloat(p.individualTotal) : 0,
        })),
      };
      if (editingBundle) {
        await adminService.updateBundle(editingBundle._id, payload);
      } else {
        await adminService.createBundle(payload);
        setPagination(prev => ({ ...prev, page: 1 }));
      }
      setIsModalOpen(false);
      fetchBundles();
    } catch (err) {
      setFormError(err.response?.data?.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminService.deleteBundle(id);
      setDeleteConfirm(null);
      fetchBundles();
    } catch (err) {
      setError('Failed to delete bundle');
    }
  };

  const statusColor = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-gray-100 text-gray-600',
    coming_soon: 'bg-blue-100 text-blue-700',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bundles</h1>
          <p className="text-gray-500 mt-1">Manage book bundles ({pagination.total} total)</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors gap-2 text-sm">
          <Plus className="w-4 h-4" />New Bundle
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-600 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Bundle</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Price</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Savings</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" /></td></tr>
              ) : bundles.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-400"><Package className="w-10 h-10 mx-auto mb-2 opacity-30" />No bundles found</td></tr>
              ) : (
                bundles.map(bundle => {
                  const priceSource = bundle.price || bundle.prices?.[0];
                  const priceObj = toPriceObject(priceSource, 'USD');
                  const priceLabel = priceObj?.retail != null ? `$${priceObj.retail.toFixed(2)}` : '—';
                  return (
                  <tr key={bundle._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{bundle.name}</p>
                      <p className="text-xs text-gray-400 font-mono">{bundle.slug}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">{bundle.type?.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{priceLabel}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{bundle.savingsPercentage != null ? `${bundle.savingsPercentage}%` : '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[bundle.status] || 'bg-gray-100 text-gray-700'}`}>
                        {bundle.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                      <button onClick={() => openEdit(bundle)} className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirm(bundle._id)} className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Bundle?</h3>
            <p className="text-gray-500 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">{editingBundle ? 'Edit Bundle' : 'New Bundle'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto p-6 space-y-4 flex-1">
              {formError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input required value={formData.name} onChange={e => setFormData(d => ({ ...d, name: e.target.value, slug: slugify(e.target.value) }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                  <input value={formData.slug} onChange={e => setFormData(d => ({ ...d, slug: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} value={formData.description} onChange={e => setFormData(d => ({ ...d, description: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select value={formData.type} onChange={e => setFormData(d => ({ ...d, type: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {BUNDLE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Buyer</label>
                  <select value={formData.targetBuyer} onChange={e => setFormData(d => ({ ...d, targetBuyer: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {TARGET_BUYERS.map(buyer => <option key={buyer} value={buyer}>{buyer.replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData(d => ({ ...d, status: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {BUNDLE_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phase</label>
                  <input type="number" min="1" max="4" value={formData.phaseNumber} onChange={e => setFormData(d => ({ ...d, phaseNumber: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
                  <input value={formData.ageRange} onChange={e => setFormData(d => ({ ...d, ageRange: e.target.value }))} placeholder="e.g. 7-10 years" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Savings %</label>
                  <input type="number" min="0" max="100" step="0.1" value={formData.savingsPercentage} onChange={e => setFormData(d => ({ ...d, savingsPercentage: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Savings Message</label>
                  <input value={formData.savingsMessage} onChange={e => setFormData(d => ({ ...d, savingsMessage: e.target.value }))} placeholder="e.g. Save 20%" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Image</label>
                  {(bundleImagePreview || formData.image) && (
                    <img
                      src={bundleImagePreview || formData.image}
                      alt="Bundle preview"
                      className="w-24 h-18 object-cover rounded-lg mb-2 border border-gray-200"
                    />
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setBundleImageFile(file);
                        setBundleImagePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-600 hover:file:bg-primary-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2">
                  <input type="checkbox" id="containsDigital" checked={formData.containsDigital} onChange={e => setFormData(d => ({ ...d, containsDigital: e.target.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700">Contains Digital Items</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" id="featured" checked={formData.featured} onChange={e => setFormData(d => ({ ...d, featured: e.target.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700">Featured Bundle</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" id="isCustomQuote" checked={formData.isCustomQuote} onChange={e => setFormData(d => ({ ...d, isCustomQuote: e.target.checked }))} className="rounded" />
                  <span className="text-sm text-gray-700">Custom Quote Required</span>
                </label>
              </div>

              <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Included Books</label>
                    <p className="text-xs text-gray-400 mt-1">Bundles are sold as grouped books, so select the exact titles included here.</p>
                  </div>
                  <button type="button" onClick={addItem} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" />Add Book
                  </button>
                </div>

                {booksLoading ? (
                  <p className="text-sm text-gray-500">Loading books…</p>
                ) : (
                  <div className="space-y-2">
                    {formData.items.map((item, i) => (
                      <div key={i} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_90px_120px_auto] gap-2 items-center">
                        <select value={item.product} onChange={e => updateItem(i, 'product', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                          <option value="">Select a book</option>
                          {booksCatalog.map((book) => (
                            <option key={book._id} value={book._id}>{formatBookOption(book)}</option>
                          ))}
                        </select>
                        <input type="number" min="1" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                        <label className="flex items-center gap-2 text-sm text-gray-700 px-2">
                          <input type="checkbox" checked={item.isRequired} onChange={e => updateItem(i, 'isRequired', e.target.checked)} className="rounded" />
                          Required
                        </label>
                        {formData.items.length > 1 && (
                          <button type="button" onClick={() => removeItem(i)} className="p-2 text-red-400 hover:text-red-600 justify-self-end"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-gray-600">{selectedBookCount} book{selectedBookCount !== 1 ? 's' : ''} selected</span>
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">Individual retail total: ${selectedRetailTotal.toFixed(2)}</span>
                    <button type="button" onClick={useSelectedBooksTotal} className="text-primary-600 hover:text-primary-700 text-xs font-medium">Use in pricing</button>
                  </div>
                </div>

                {hasDuplicateBooks && <p className="text-xs text-red-600">Duplicate books selected. Use quantity instead.</p>}
              </div>

              {/* Prices */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Pricing</label>
                  <button type="button" onClick={addPrice} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" />Add Currency
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.prices.map((price, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
                      <select value={price.currency} onChange={e => updatePrice(i, 'currency', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input type="number" step="0.01" min="0" placeholder="Bundle price" value={price.bundlePrice} onChange={e => updatePrice(i, 'bundlePrice', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                      <input type="number" step="0.01" min="0" placeholder="Individual total" value={price.individualTotal} onChange={e => updatePrice(i, 'individualTotal', e.target.value)} className="border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                      {formData.prices.length > 1 && (
                        <button type="button" onClick={() => removePrice(i)} className="p-2 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">Currency / Bundle Price / Individual Total</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={formLoading} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 disabled:opacity-60 flex items-center gap-2">
                  {formLoading && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                  {editingBundle ? 'Save Changes' : 'Create Bundle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BundlesPage;
